import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { communityApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import LoadingSpinner from '../components/LoadingSpinner'

type CommunityAccess = 'none' | 'read' | 'full'

// Types for API responses
interface CommentAuthor {
  id?: string
  displayName: string
  isBot?: boolean
}

interface Comment {
  id: string
  content: string
  createdAt: string
  author: CommentAuthor | null
  userId?: string
  verificationResult?: {
    verdict: string
    confidence: number
    reasoning: string
    sources?: Array<{ url: string; title: string; credibilityScore?: number }>
  } | null
}

interface CommentsResponse {
  data: {
    comments: Comment[]
  }
}

interface PostAuthor {
  id: string | null
  displayName: string
  communityLevel: string
  reputationScore: number
}

interface Post {
  id: string
  content: string
  createdAt: string
  updatedAt: string
  author: PostAuthor | null
  votes: number
  upvotes: number
  downvotes: number
  userVote: number | string | null
  commentsCount: number
  commentCount: number
  userId?: string
}

interface PostsResponse {
  data: {
    posts: Post[]
    pagination: {
      total: number
      limit: number
      offset: number
    }
  }
}

interface Contributor {
  id: string
  displayName: string
  reputationScore: number
  postsCount: number
  postCount: number
}

interface CommunityStats {
  totalPosts: number
  totalComments: number
  activeMembers: number
  topContributors: Contributor[]
}


// Get community access level based on tier
function getCommunityAccess(tier: string | undefined): CommunityAccess {
  if (tier === 'professional') return 'full'
  if (tier === 'premium') return 'read'
  return 'none' // free, basic, or undefined
}

// Comments Section Component
const CommentsSection: React.FC<{ postId: string; canWrite: boolean }> = ({ postId, canWrite }) => {
  const [commentText, setCommentText] = useState('')
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { authState } = useAuth()
  const currentUser = authState.status === 'authenticated' ? authState.user : null

  // Fetch comments for this post
  const { data: commentsData, isLoading: commentsLoading } = useQuery({
    queryKey: ['community', 'comments', postId],
    queryFn: () => communityApi.getComments(postId),
    enabled: !!postId
  })

  const comments = commentsData?.data?.comments || []

  // Add comment mutation with optimistic updates
  const addCommentMutation = useMutation({
    mutationFn: ({ content }: { content: string }) =>
      communityApi.addComment(postId, content),
    onMutate: async ({ content }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['community', 'comments', postId] })

      // Snapshot previous value
      const previousComments = queryClient.getQueryData(['community', 'comments', postId])

      // Optimistically update comments
      queryClient.setQueryData<CommentsResponse>(['community', 'comments', postId], (old) => {
        if (!old) return old

        const newComment: Comment = {
          id: `temp-${Date.now()}`,
          content,
          createdAt: new Date().toISOString(),
          author: { displayName: 'You' }
        }

        return {
          ...old,
          data: {
            ...old.data,
            comments: [...(old.data?.comments || []), newComment]
          }
        }
      })

      return { previousComments }
    },
    onSuccess: () => {
      setCommentText('')
      // Refresh comments to get real server data
      queryClient.invalidateQueries({ queryKey: ['community', 'comments', postId] })
      // Update post count in main posts list
      queryClient.setQueryData<PostsResponse>(['community', 'posts'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            posts: old.data.posts.map((post) => {
              if (post.id === postId) {
                return { ...post, commentCount: post.commentCount + 1 }
              }
              return post
            })
          }
        }
      })
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData(['community', 'comments', postId], context.previousComments)
      }
    }
  })

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => communityApi.deleteComment(commentId),
    onSuccess: (_data, commentId) => {
      // Remove comment from the list optimistically
      queryClient.setQueryData<CommentsResponse>(['community', 'comments', postId], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            comments: old.data.comments.filter((comment) => comment.id !== commentId)
          }
        }
      })
      // Update post comment count
      queryClient.setQueryData<PostsResponse>(['community', 'posts'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            posts: old.data.posts.map((post) => {
              if (post.id === postId) {
                return { ...post, commentCount: Math.max(0, post.commentCount - 1) }
              }
              return post
            })
          }
        }
      })
      setDeletingCommentId(null)
    }
  })

  const handleDeleteComment = (commentId: string) => {
    setDeletingCommentId(commentId)
  }

  const confirmDeleteComment = (commentId: string) => {
    deleteCommentMutation.mutate(commentId)
  }

  const cancelDeleteComment = () => {
    setDeletingCommentId(null)
  }

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim()) return
    addCommentMutation.mutate({
      content: commentText.trim()
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="mt-4 pt-4 border-t border-paper-200">
      {/* Add Comment Form - only for Professional */}
      {canWrite && (
      <form onSubmit={handleSubmitComment} className="mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-start">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            rows={3}
            disabled={addCommentMutation.isPending}
            className="flex-1 p-2 border border-paper-200 rounded-sm text-sm text-ink-900 placeholder:text-ink-300 resize-none focus:outline-none focus:border-accent-500"
          />
          <button
            type="submit"
            disabled={addCommentMutation.isPending || !commentText.trim()}
            className="w-full sm:w-auto px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-sm font-medium disabled:bg-ink-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {addCommentMutation.isPending ? 'Posting...' : 'Comment'}
          </button>
        </div>

      </form>
      )}

      {/* Comments List */}
      {commentsLoading ? (
        <div className="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      ) : comments.length > 0 ? (
        <div className="flex flex-col gap-3">
          {comments.map((comment: Comment) => (
            <div
              key={comment.id}
              className={`flex gap-3 p-3 rounded-sm ${
                comment.author?.isBot ? 'bg-brand-50 border border-brand-100' : 'bg-paper-50'
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  comment.author?.isBot ? 'bg-brand-800' : 'bg-brand-950'
                }`}
              >
                <span className="text-white font-medium text-xs">
                  {comment.author?.isBot ? 'T' : (comment.author?.displayName?.charAt(0)?.toUpperCase() || 'U')}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-ink-900">
                      {comment.author?.displayName || 'Deleted User'}
                    </span>
                    {comment.author?.isBot && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-brand-100 text-brand-800 rounded-full font-semibold">
                        Bot
                      </span>
                    )}
                    <span className="text-xs text-ink-400">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  {/* Delete button/confirmation for comment owner (not for bot comments) */}
                  {currentUser && comment.author?.id === currentUser.id && !comment.author?.isBot && (
                    <div className="flex items-center gap-2">
                      {deletingCommentId === comment.id ? (
                        <>
                          <button
                            onClick={() => confirmDeleteComment(comment.id)}
                            disabled={deleteCommentMutation.isPending}
                            className="px-2 py-1 text-xs text-white bg-danger-500 rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deleteCommentMutation.isPending ? '...' : 'Delete'}
                          </button>
                          <button
                            onClick={cancelDeleteComment}
                            disabled={deleteCommentMutation.isPending}
                            className="px-2 py-1 text-xs text-ink-400 bg-transparent border border-paper-200 rounded-sm disabled:cursor-not-allowed"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="px-2 py-1 text-xs text-danger-500 bg-transparent border border-danger-100 rounded-sm hover:bg-danger-100"
                          title="Delete comment"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm text-ink-600 leading-snug m-0 whitespace-pre-wrap">
                  {comment.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-6 text-ink-400 text-sm">
          No comments yet. Be the first to comment!
        </div>
      )}
    </div>
  )
}

const CommunityPage: React.FC = () => {
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [postTitle, setPostTitle] = useState('')
  const [postContent, setPostContent] = useState('')
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({})
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null)
  const [sharingPostId, setSharingPostId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { authState } = useAuth()
  const currentUser = authState.status === 'authenticated' ? authState.user : null
  const communityAccess = getCommunityAccess(currentUser?.subscriptionTier)

  // Fetch community posts
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['community', 'posts'],
    queryFn: () => communityApi.getPosts(50, 0),
    retry: 1
  })

  // Fetch community stats
  const { data: statsData } = useQuery<{ data: CommunityStats }>({
    queryKey: ['community', 'stats'],
    queryFn: () => communityApi.getStats(),
    retry: 1
  })

  const posts = postsData?.data?.posts || []
  const stats = statsData?.data || { activeMembers: 0, totalPosts: 0, topContributors: [] }

  // Create post mutation
  const createPostMutation = useMutation({
    mutationFn: ({ content }: { content: string }) =>
      communityApi.createPost(content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'posts'] })
      queryClient.invalidateQueries({ queryKey: ['community', 'stats'] })
      setShowCreatePost(false)
      setPostTitle('')
      setPostContent('')
    }
  })

  // Vote mutation with optimistic updates
  const voteMutation = useMutation({
    mutationFn: ({ postId, vote }: { postId: string; vote: 'up' | 'down' }) =>
      communityApi.votePost(postId, vote),
    onMutate: async ({ postId, vote }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['community', 'posts'] })

      // Snapshot previous value
      const previousPosts = queryClient.getQueryData(['community', 'posts'])

      // Optimistically update
      queryClient.setQueryData<PostsResponse>(['community', 'posts'], (old) => {
        if (!old) return old

        return {
          ...old,
          data: {
            ...old.data,
            posts: old.data.posts.map((post) => {
              if (post.id === postId) {
                const updatedPost = { ...post }
                if (vote === 'up') {
                  updatedPost.upvotes += 1
                } else {
                  updatedPost.downvotes += 1
                }
                return updatedPost
              }
              return post
            })
          }
        }
      })

      return { previousPosts }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousPosts) {
        queryClient.setQueryData(['community', 'posts'], context.previousPosts)
      }
    },
    onSuccess: (data, { postId }) => {
      // Update with real server response
      queryClient.setQueryData<PostsResponse>(['community', 'posts'], (old) => {
        if (!old) return old

        return {
          ...old,
          data: {
            ...old.data,
            posts: old.data.posts.map((post) => {
              if (post.id === postId) {
                return {
                  ...post,
                  upvotes: data.upvotes,
                  downvotes: data.downvotes,
                  userVote: data.userVote
                }
              }
              return post
            })
          }
        }
      })
    }
  })

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => communityApi.deletePost(postId),
    onSuccess: (_data, postId) => {
      // Remove post from the list optimistically
      queryClient.setQueryData<PostsResponse>(['community', 'posts'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            posts: old.data.posts.filter((post) => post.id !== postId)
          }
        }
      })
      // Refresh stats
      queryClient.invalidateQueries({ queryKey: ['community', 'stats'] })
      setDeletingPostId(null)
    }
  })

  const handleDeletePost = (postId: string) => {
    setDeletingPostId(postId)
  }

  const confirmDeletePost = (postId: string) => {
    deletePostMutation.mutate(postId)
  }

  const cancelDeletePost = () => {
    setDeletingPostId(null)
  }

  const handleSharePost = (post: Post) => {
    // Always show inline options first, giving users choice
    setSharingPostId(post.id)
  }

  const handleNativeShare = async (post: Post) => {
    if (navigator.share) {
      const shareData = {
        title: `${post.author?.displayName || 'Someone'} shared on Treu Community`,
        text: post.content.length > 100 ? post.content.substring(0, 100) + '...' : post.content,
        url: `${window.location.origin}/community#post-${post.id}`
      }

      try {
        await navigator.share(shareData)
        setSharingPostId(null)
      } catch {
        // User cancelled share dialog - this is expected behavior
      }
    }
  }

  const copyToClipboard = async (post: Post) => {
    const shareText = `${post.author?.displayName || 'Someone'} shared on Treu Community\n\n${post.content}\n\n${window.location.origin}/community#post-${post.id}`

    try {
      await navigator.clipboard.writeText(shareText)
      setSharingPostId(null)
      // Visual feedback - you could improve this with a toast
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const shareToSocial = (platform: string, post: Post) => {
    const shareText = post.content.length > 100 ? post.content.substring(0, 100) + '...' : post.content
    const shareUrl = `${window.location.origin}/community#post-${post.id}`
    const title = `${post.author?.displayName || 'Someone'} shared on Treu Community`

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      reddit: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`
    }

    const url = urls[platform as keyof typeof urls]
    if (url) {
      window.open(url, '_blank', 'width=600,height=400')
      setSharingPostId(null)
    }
  }

  const handleCreatePost = (e: React.FormEvent) => {
    e.preventDefault()
    if (!postContent.trim()) return

    // Combine title and content for backend
    const combinedContent = postTitle.trim() ? `${postTitle.trim()}\n\n${postContent.trim()}` : postContent.trim()

    createPostMutation.mutate({
      content: combinedContent
    })
  }

  const handleVote = (postId: string, vote: 'up' | 'down') => {
    voteMutation.mutate({ postId, vote })
  }

  const toggleComments = (postId: string) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }))
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    return date.toLocaleDateString()
  }

  // Show upgrade prompt for Free/Basic users
  if (communityAccess === 'none') {
    return (
      <div className="bg-paper-0 px-4 py-4 min-h-[80vh] flex items-center justify-center">
        <div className="max-w-[480px] w-full text-center px-6 py-10 md:px-10 md:py-12 bg-white rounded-lg shadow-card border border-paper-200">
          <div className="w-16 h-16 bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-6">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-ink-900 mb-3 tracking-tight">
            Discerning Minds
          </h1>
          <p className="text-ink-600 text-base mb-8 leading-relaxed">
            Connect with fellow truth-seekers, share insights, and discuss verifications with like-minded individuals.
          </p>
          <div className="bg-paper-50 rounded-md px-5 py-4 mb-8 text-left">
            <div className="flex items-center mb-3">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mr-3"></div>
              <span className="text-sm text-ink-600">
                <strong className="text-ink-900">Premium</strong> — Read and explore discussions
              </span>
            </div>
            <div className="flex items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mr-3"></div>
              <span className="text-sm text-ink-600">
                <strong className="text-ink-900">Professional</strong> — Post and comment
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate('/profile#upgrade')}
            className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-base font-semibold transition-colors"
          >
            Unlock Access
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-paper-0 px-4 py-4">
      {/* Page Heading */}
      <div className="mb-8 border-b border-paper-200 pb-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-ink-900 m-0 text-left tracking-tight">
          Discerning Minds
        </h1>
        <p className="text-ink-400 text-base mt-2 font-normal">
          Connect with fellow truth-seekers and critical thinkers
        </p>
      </div>

      {/* New Post Button - only for Professional */}
      {communityAccess === 'full' && (
        <div className="flex justify-end mb-8">
          <button
            onClick={() => setShowCreatePost(true)}
            className="w-full sm:w-auto px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-sm font-medium"
          >
            New Post
          </button>
        </div>
      )}

      {/* Read-only notice for Premium users */}
      {communityAccess === 'read' && (
        <div className="bg-brand-50 border border-brand-100 rounded-md px-4 py-3 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="m-0 text-sm text-brand-800">
            You have read-only access. Upgrade to Professional to post and comment.
          </p>
          <button
            onClick={() => navigate('/profile#upgrade')}
            className="w-full sm:w-auto px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-xs font-medium shrink-0"
          >
            Upgrade
          </button>
        </div>
      )}

      {/* Community Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
        <div className="bg-white border border-paper-200 rounded-lg shadow-card p-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-brand-950 rounded-md flex items-center justify-center text-white font-semibold">
              M
            </div>
            <div>
              <p className="text-sm text-ink-400">Active Members</p>
              <p className="text-lg font-semibold text-ink-900">{stats.activeMembers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-paper-200 rounded-lg shadow-card p-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-brand-800 rounded-md flex items-center justify-center text-white font-semibold">
              P
            </div>
            <div>
              <p className="text-sm text-ink-400">Total Posts</p>
              <p className="text-lg font-semibold text-ink-900">{stats.totalPosts}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-paper-200 rounded-lg shadow-card p-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-brand-700 rounded-md flex items-center justify-center text-white font-semibold">
              T
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink-400">Top Contributors</p>
              {stats.topContributors.length > 0 ? (
                <div className="text-xs text-ink-900 mt-1">
                  {stats.topContributors.slice(0, 2).map((contributor: Contributor) => (
                    <div key={contributor.id} className="flex justify-between items-center mb-1 gap-2">
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{contributor.displayName}</span>
                      <span className="text-ink-400 shrink-0">{contributor.postCount} posts</span>
                    </div>
                  ))}
                  {stats.topContributors.length > 2 && (
                    <div className="text-ink-400 text-[10px]">
                      +{stats.topContributors.length - 2} more
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-lg font-semibold text-ink-300">No contributors yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className="fixed inset-0 bg-brand-950/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-overlay p-6 w-full max-w-md">
            {/* Create Post Modal Title */}
            <h2 className="text-lg font-semibold text-ink-900 mb-4">Create New Post</h2>
            <form onSubmit={handleCreatePost} className="flex flex-col gap-4">
              <div>
                <label htmlFor="postTitle" className="block text-sm font-medium text-ink-700 mb-1">
                  Title
                </label>
                <input
                  id="postTitle"
                  type="text"
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                  placeholder="Post title (optional)"
                  disabled={createPostMutation.isPending}
                  className="w-full p-3 border border-paper-200 rounded-sm text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:border-accent-500"
                />
              </div>
              <div>
                <label htmlFor="postContent" className="block text-sm font-medium text-ink-700 mb-1">
                  Content
                </label>
                <textarea
                  id="postContent"
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  placeholder="Share your thoughts with the community..."
                  rows={4}
                  disabled={createPostMutation.isPending}
                  className="w-full p-3 border border-paper-200 rounded-sm text-sm text-ink-900 placeholder:text-ink-300 resize-none leading-normal focus:outline-none focus:border-accent-500"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={createPostMutation.isPending || !postContent.trim()}
                  className="w-full sm:w-auto px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-sm font-medium disabled:bg-ink-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createPostMutation.isPending ? 'Posting...' : 'Post'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreatePost(false)}
                  disabled={createPostMutation.isPending}
                  className="w-full sm:w-auto px-6 py-3 bg-white text-ink-600 border border-paper-200 rounded-md text-sm hover:bg-paper-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Community Posts */}
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : posts.length ? (
          posts.map((post: Post) => (
            <div key={post.id} className="bg-white border border-paper-200 rounded-lg shadow-card p-5">
              {/* Post Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-brand-950 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-medium text-sm">
                      {post.author?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-ink-900">{post.author?.displayName || 'Deleted User'}</p>
                      <span className="text-xs px-2 py-0.5 bg-brand-50 text-brand-800 rounded-full">
                        {(post.author?.communityLevel || 'newcomer').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                    <p className="text-xs text-ink-400">{formatDate(post.createdAt)}</p>
                  </div>
                </div>
                {/* Delete button/confirmation for post owner */}
                {currentUser && post.author?.id === currentUser.id && (
                  <div className="flex items-center gap-2">
                    {deletingPostId === post.id ? (
                      <>
                        <button
                          onClick={() => confirmDeletePost(post.id)}
                          disabled={deletePostMutation.isPending}
                          className="px-3 py-2 text-xs text-white bg-danger-500 rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deletePostMutation.isPending ? 'Deleting...' : 'Delete Post'}
                        </button>
                        <button
                          onClick={cancelDeletePost}
                          disabled={deletePostMutation.isPending}
                          className="px-3 py-2 text-xs text-ink-400 bg-transparent border border-paper-200 rounded-sm disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="p-2 text-sm text-danger-500 bg-transparent border border-danger-100 rounded-sm hover:bg-danger-100"
                        title="Delete post"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Post Content */}
              <div className="mb-4">
                <p className="text-ink-600 text-sm leading-normal whitespace-pre-wrap">{post.content}</p>
              </div>

              {/* Post Actions */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-paper-200">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleVote(post.id, 'up')}
                      className="p-2 rounded-sm border-0 bg-transparent text-ink-400 hover:bg-paper-100 hover:text-ink-600 cursor-pointer"
                    >
                      ↑
                    </button>
                    <span className="text-sm font-medium text-ink-700">{post.upvotes - post.downvotes}</span>
                    <button
                      onClick={() => handleVote(post.id, 'down')}
                      className="p-2 rounded-sm border-0 bg-transparent text-ink-400 hover:bg-paper-100 hover:text-ink-600 cursor-pointer"
                    >
                      ↓
                    </button>
                  </div>

                  <button
                    onClick={() => toggleComments(post.id)}
                    className={`flex items-center gap-1 text-sm border-0 bg-transparent cursor-pointer ${
                      expandedComments[post.id] ? 'text-accent-600' : 'text-ink-400 hover:text-ink-600'
                    }`}
                  >
                    <span>💬</span>
                    {post.commentCount} {post.commentCount === 1 ? 'comment' : 'comments'}
                  </button>
                </div>

                {sharingPostId === post.id ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => copyToClipboard(post)}
                      className="px-2 py-1 text-xs text-ink-600 bg-paper-50 border border-paper-200 rounded-sm hover:bg-paper-100"
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={() => shareToSocial('twitter', post)}
                      className="px-2 py-1 text-xs text-ink-600 bg-paper-50 border border-paper-200 rounded-sm hover:bg-paper-100"
                    >
                      𝕏
                    </button>
                    <button
                      onClick={() => shareToSocial('reddit', post)}
                      className="px-2 py-1 text-xs text-ink-600 bg-paper-50 border border-paper-200 rounded-sm hover:bg-paper-100"
                    >
                      Reddit
                    </button>
                    {'share' in navigator && (
                      <button
                        onClick={() => handleNativeShare(post)}
                        className="px-2 py-1 text-xs text-ink-600 bg-paper-50 border border-paper-200 rounded-sm hover:bg-paper-100"
                      >
                        More Apps
                      </button>
                    )}
                    <button
                      onClick={() => setSharingPostId(null)}
                      className="px-2 py-1 text-xs text-ink-400 bg-transparent border-0 hover:text-ink-600 cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleSharePost(post)}
                    className="text-ink-400 text-sm border-0 bg-transparent hover:text-ink-600 cursor-pointer"
                  >
                    Share
                  </button>
                )}
              </div>

              {/* Comments Section */}
              {expandedComments[post.id] && <CommentsSection postId={post.id} canWrite={communityAccess === 'full'} />}
            </div>
          ))
        ) : (
          <div className="bg-white border border-paper-200 rounded-lg shadow-card text-center p-8">
            <h3 className="text-lg font-medium text-ink-900 mb-2">No posts yet</h3>
            <p className="text-ink-400 mb-4">Be the first to start a conversation!</p>
            <button
              onClick={() => setShowCreatePost(true)}
              className="w-full sm:w-auto px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-md text-sm font-medium"
            >
              Create First Post
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CommunityPage
